import React, { useState, useEffect, useMemo, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  getProjectById,
  getProjectBlocksPersonHours,
  getProjectSectionsPersonHours,
  getBlockSectionsPersonHours,
  getSectionPersonHours,
  getProjectPersonHours,
  exportProjectPersonHours,
} from "../../../../api/projectApi";
import "./ProjectAnalysis.css";

const ProjectAnalysis = ({ projectId }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [analysisData, setAnalysisData] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [error, setError] = useState(null);

  // Состояния для отслеживания доступности различных типов данных
  const [dataAvailability, setDataAvailability] = useState({
    blocks: true,
    sections: true,
    employees: true,
  });

  // Filter states
  const [displayMode, setDisplayMode] = useState("hoursAndDays"); // 'hours', 'days', 'hoursAndDays'
  const [groupBy, setGroupBy] = useState("block"); // 'block', 'section', 'employee'

  // Период: null = за весь проект; иначе фильтр по датам. Блок выбора периода сворачивается.
  const getDefaultPeriodFrom = () => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  };
  const getDefaultPeriodTo = () => new Date().toISOString().slice(0, 10);
  const [periodDateFrom, setPeriodDateFrom] = useState(getDefaultPeriodFrom);
  const [periodDateTo, setPeriodDateTo] = useState(getDefaultPeriodTo);
  const [appliedPeriodFrom, setAppliedPeriodFrom] = useState(null);
  const [appliedPeriodTo, setAppliedPeriodTo] = useState(null);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [exportExcelLoading, setExportExcelLoading] = useState(false);

  const periodParams = useMemo(
    () =>
      appliedPeriodFrom && appliedPeriodTo
        ? { date_from: appliedPeriodFrom, date_to: appliedPeriodTo }
        : {},
    [appliedPeriodFrom, appliedPeriodTo],
  );

  const applyPeriod = () => {
    setAppliedPeriodFrom(periodDateFrom);
    setAppliedPeriodTo(periodDateTo);
    setPeriodOpen(false);
  };

  const resetPeriod = () => {
    setAppliedPeriodFrom(null);
    setAppliedPeriodTo(null);
    setPeriodOpen(false);
  };

  const openPeriodPanel = () => {
    if (appliedPeriodFrom && appliedPeriodTo) {
      setPeriodDateFrom(appliedPeriodFrom);
      setPeriodDateTo(appliedPeriodTo);
    }
    setPeriodOpen(true);
  };

  // Функция для проверки доступности данных
  const checkDataAvailability = async (projectId) => {
    const availability = {
      blocks: false,
      sections: false,
      employees: false,
    };

    // Проверяем доступность блоков
    try {
      await getProjectBlocksPersonHours(projectId);
      availability.blocks = true;
    } catch (error) {
      console.warn("Блоки недоступны:", error.message);
    }

    // Проверяем доступность разделов
    try {
      await getProjectSectionsPersonHours(projectId);
      availability.sections = true;
    } catch (error) {
      console.warn("Разделы недоступны:", error.message);
    }

    // Проверяем доступность сотрудников
    try {
      await getProjectPersonHours(projectId);
      availability.employees = true;
    } catch (error) {
      console.warn("Данные сотрудников недоступны:", error.message);
    }

    setDataAvailability(availability);
    return availability;
  };

  // Функция для определения доступной группировки по умолчанию
  const getDefaultGroupBy = (availability) => {
    if (availability.blocks) return "block";
    if (availability.sections) return "section";
    if (availability.employees) return "employee";
    return "block"; // fallback
  };

  // Сопоставление значения groupBy (singular) к ключам доступности (plural)
  const mapGroupByToAvailabilityKey = (value) => {
    switch (value) {
      case "block":
        return "blocks";
      case "section":
        return "sections";
      case "employee":
        return "employees";
      default:
        return "blocks";
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Получаем основную информацию о проекте
        const projectData = await getProjectById(projectId);
        setProject(projectData);

        // Проверяем доступность всех типов данных
        const availability = await checkDataAvailability(projectId);

        // Если ни один тип данных недоступен, показываем общую ошибку
        if (
          !availability.blocks &&
          !availability.sections &&
          !availability.employees
        ) {
          setError("Для данного проекта нет доступных данных о трудозатратах");
          setLoading(false);
          return;
        }

        // Если текущая группировка недоступна, переключаемся на доступную
        const availabilityKey = mapGroupByToAvailabilityKey(groupBy);
        if (!availability[availabilityKey]) {
          const defaultGroupBy = getDefaultGroupBy(availability);
          setGroupBy(defaultGroupBy);
        }

        // Получаем данные о трудозатратах в зависимости от выбранной группировки
        let data = [];

        if (groupBy === "block" && availability.blocks) {
          const blocksRaw = await getProjectBlocksPersonHours(
            projectId,
            periodParams,
          );
          const blocksData = Array.isArray(blocksRaw) ? blocksRaw : [];
          const blocksWithSections = await Promise.all(
            blocksData.map(async (block) => {
              try {
                const sectionsRaw = await getBlockSectionsPersonHours(
                  projectId,
                  block.id,
                );
                const blockSectionsData = Array.isArray(sectionsRaw)
                  ? sectionsRaw
                  : [];
                const sectionsWithEmployees = await Promise.all(
                  blockSectionsData.map(async (section) => {
                    try {
                      const employeesData = await getSectionPersonHours(
                        projectId,
                        block.id,
                        section.id,
                        periodParams,
                      );
                      return {
                        ...section,
                        employees: employeesData || [],
                      };
                    } catch (error) {
                      return { ...section, employees: [] };
                    }
                  }),
                );
                return { ...block, sections: sectionsWithEmployees };
              } catch (error) {
                return { ...block, sections: [] };
              }
            }),
          );
          // Итоги по разделам — из сотрудников (разделы по блоку без периода в API)
          blocksWithSections.forEach((block) => {
            (block.sections || []).forEach((section) => {
              const emp = section.employees || [];
              section.total_hours = emp.reduce(
                (s, e) => s + (e.total_hours || 0),
                0,
              );
              section.total_days = emp.reduce(
                (s, e) => s + (e.total_days || 0),
                0,
              );
              section.total_cost = emp.reduce(
                (s, e) => s + (e.total_cost || 0),
                0,
              );
            });
            // Если по блоку пришли нули (например при 404 по разделам), подставляем сумму по разделам
            const hasSectionTotals = (block.sections || []).length > 0;
            const sumHours = (block.sections || []).reduce(
              (s, sec) => s + (sec.total_hours || 0),
              0,
            );
            const sumDays = (block.sections || []).reduce(
              (s, sec) => s + (sec.total_days || 0),
              0,
            );
            const sumCost = (block.sections || []).reduce(
              (s, sec) => s + (sec.total_cost || 0),
              0,
            );
            if (
              hasSectionTotals &&
              (Number(block.total_hours) === 0 ||
                Number(block.total_days) === 0 ||
                Number(block.total_cost) === 0)
            ) {
              block.total_hours = sumHours;
              block.total_days = sumDays;
              block.total_cost = sumCost;
            }
          });
          data = blocksWithSections;
        } else if (groupBy === "section" && availability.sections) {
          const sectionsRaw = await getProjectSectionsPersonHours(
            projectId,
            periodParams,
          );
          const sectionsData = Array.isArray(sectionsRaw) ? sectionsRaw : [];
          data = sectionsData.map((section) => ({
            ...section,
            sections: [],
          }));
        } else if (groupBy === "employee" && availability.employees) {
          const employeesData = await getProjectPersonHours(
            projectId,
            periodParams,
          );
          data = (employeesData || []).map((employee) => ({
            ...employee,
            sections: [],
          }));
        }

        setAnalysisData(data);
      } catch (error) {
        console.error("Ошибка при получении данных:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, groupBy, periodParams]);

  const toggleRowExpansion = (rowId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleExportExcel = async () => {
    setExportExcelLoading(true);
    try {
      await exportProjectPersonHours(projectId, periodParams);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setExportExcelLoading(false);
    }
  };

  const totals = analysisData.reduce(
    (acc, item) => {
      acc.hours += item.total_hours || 0;
      acc.days += item.total_days || 0;
      acc.cost += item.total_cost || 0;
      return acc;
    },
    { hours: 0, days: 0, cost: 0 },
  );

  if (loading) {
    return <div>Загрузка...</div>;
  }

  if (error) {
    return (
      <div className="error-message">
        <h3>Ошибка при загрузке данных</h3>
        <p>{error}</p>
        <button
          onClick={() => {
            setError(null);
            setLoading(true);
            // Перезапускаем useEffect
            window.location.reload();
          }}
          style={{
            padding: "8px 16px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Попробовать снова
        </button>
      </div>
    );
  }

  return (
    <div className="analysis-container">
      <div className="analysis-content-wrapper">
        <main className="analysis-main">
          <div className="analysis-table-header">
            <h3>Анализ трудозатрат</h3>
            <div className="analysis-period">
              <span className="analysis-period-label">Период:</span>
              {!periodOpen ? (
                <>
                  <span className="analysis-period-value">
                    {appliedPeriodFrom && appliedPeriodTo
                      ? `${appliedPeriodFrom} — ${appliedPeriodTo}`
                      : "за весь проект"}
                  </span>
                  <button
                    type="button"
                    className="analysis-period-toggle"
                    onClick={openPeriodPanel}
                  >
                    {appliedPeriodFrom && appliedPeriodTo
                      ? "Изменить"
                      : "Указать период"}
                  </button>
                </>
              ) : (
                <>
                  <label className="analysis-period-field">
                    <span>С</span>
                    <input
                      type="date"
                      value={periodDateFrom}
                      onChange={(e) => setPeriodDateFrom(e.target.value)}
                      max={periodDateTo}
                    />
                  </label>
                  <label className="analysis-period-field">
                    <span>По</span>
                    <input
                      type="date"
                      value={periodDateTo}
                      onChange={(e) => setPeriodDateTo(e.target.value)}
                      min={periodDateFrom}
                    />
                  </label>
                  <button
                    type="button"
                    className="analysis-period-apply"
                    onClick={applyPeriod}
                  >
                    Применить
                  </button>
                  <button
                    type="button"
                    className="analysis-period-reset"
                    onClick={resetPeriod}
                  >
                    Сбросить
                  </button>
                </>
              )}
            </div>
          </div>
          <table className="analysis-table">
            <thead>
              <tr>
                <th>
                  {groupBy === "block"
                    ? "Блок"
                    : groupBy === "section"
                      ? "Раздел"
                      : "Сотрудник"}
                </th>
                <th>Трудозатраты в часах/в днях</th>
                <th>Прямые затраты в тенге</th>
              </tr>
            </thead>
            <tbody>
              {analysisData.map((item) => (
                <Fragment key={item.id}>
                  <tr
                    className="block-row"
                    onClick={() => toggleRowExpansion(item.id)}
                  >
                    <td>
                      <div className="expand-cell">
                        {groupBy === "block" &&
                          item.sections &&
                          item.sections.length > 0 && (
                            <button
                              className={`expand-btn ${
                                expandedRows.has(item.id) ? "expanded" : ""
                              }`}
                            >
                              &#x25B6;
                            </button>
                          )}
                        {item.name}
                      </div>
                    </td>
                    <td>
                      <div className="analysis-table-cell-split">
                        {["hours", "hoursAndDays"].includes(displayMode) && (
                          <span>🕒 {item.total_hours || 0}</span>
                        )}
                        {["days", "hoursAndDays"].includes(displayMode) && (
                          <span>📅 {(item.total_days || 0).toFixed(2)}</span>
                        )}
                      </div>
                    </td>
                    <td>₸ {(item.total_cost || 0).toLocaleString("ru-RU")}</td>
                  </tr>
                  {groupBy === "block" &&
                    expandedRows.has(item.id) &&
                    item.sections &&
                    item.sections.map((section) => (
                      <Fragment key={`${item.id}_${section.id}`}>
                        <tr
                          className="section-row"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowExpansion(`${item.id}_${section.id}`);
                          }}
                        >
                          <td>
                            <div className="expand-cell">
                              {section.employees &&
                                section.employees.length > 0 && (
                                  <button
                                    className={`expand-btn ${
                                      expandedRows.has(
                                        `${item.id}_${section.id}`,
                                      )
                                        ? "expanded"
                                        : ""
                                    }`}
                                  >
                                    &#x25B6;
                                  </button>
                                )}
                              {section.name}
                            </div>
                          </td>
                          <td>
                            <div className="analysis-table-cell-split">
                              {["hours", "hoursAndDays"].includes(
                                displayMode,
                              ) && <span>🕒 {section.total_hours || 0}</span>}
                              {["days", "hoursAndDays"].includes(
                                displayMode,
                              ) && (
                                <span>
                                  📅 {(section.total_days || 0).toFixed(2)}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            ₸{" "}
                            {(section.total_cost || 0).toLocaleString("ru-RU")}
                          </td>
                        </tr>
                        {expandedRows.has(`${item.id}_${section.id}`) &&
                          section.employees &&
                          section.employees.map((employee) => (
                            <tr
                              key={`${item.id}_${section.id}_${employee.id}`}
                              className="analysis-employee-row"
                            >
                              <td>{employee.name}</td>
                              <td>
                                <div className="analysis-table-cell-split">
                                  {["hours", "hoursAndDays"].includes(
                                    displayMode,
                                  ) && (
                                    <span>🕒 {employee.total_hours || 0}</span>
                                  )}
                                  {["days", "hoursAndDays"].includes(
                                    displayMode,
                                  ) && (
                                    <span>
                                      📅 {(employee.total_days || 0).toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                ₸{" "}
                                {(employee.total_cost || 0).toLocaleString(
                                  "ru-RU",
                                )}
                              </td>
                            </tr>
                          ))}
                      </Fragment>
                    ))}
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>ИТОГО</td>
                <td>
                  <div className="analysis-table-cell-split">
                    {["hours", "hoursAndDays"].includes(displayMode) && (
                      <span>🕒 {totals.hours}</span>
                    )}
                    {["days", "hoursAndDays"].includes(displayMode) && (
                      <span>📅 {totals.days.toFixed(2)}</span>
                    )}
                  </div>
                </td>
                <td>₸ {totals.cost.toLocaleString("ru-RU")}</td>
              </tr>
            </tfoot>
          </table>
        </main>
        <aside className="analysis-sidebar">
          <h4>Фильтрация</h4>
          <div className="filter-group">
            <h5>Отображать трудозатраты</h5>
            <label>
              <input
                type="radio"
                value="hours"
                checked={displayMode === "hours"}
                onChange={(e) => setDisplayMode(e.target.value)}
              />
              <span className="checkmark"></span>
              Только в часах
            </label>
            <label>
              <input
                type="radio"
                value="days"
                checked={displayMode === "days"}
                onChange={(e) => setDisplayMode(e.target.value)}
              />
              <span className="checkmark"></span>
              Только в днях
            </label>
            <label>
              <input
                type="radio"
                value="hoursAndDays"
                checked={displayMode === "hoursAndDays"}
                onChange={(e) => setDisplayMode(e.target.value)}
              />
              <span className="checkmark"></span>В часах и днях
            </label>
          </div>
          <div className="filter-group">
            <h5>Группировать по</h5>
            {/* Показываем чекбокс "Блоку" только если блоки доступны */}
            {dataAvailability.blocks && (
              <label>
                <input
                  type="radio"
                  value="block"
                  checked={groupBy === "block"}
                  onChange={(e) => setGroupBy(e.target.value)}
                />
                <span className="checkmark"></span>
                Блоку
              </label>
            )}
            {/* Показываем чекбокс "Разделу" только если разделы доступны */}
            {dataAvailability.sections && (
              <label>
                <input
                  type="radio"
                  value="section"
                  checked={groupBy === "section"}
                  onChange={(e) => setGroupBy(e.target.value)}
                />
                <span className="checkmark"></span>
                Разделу
              </label>
            )}
            {/* Показываем чекбокс "Сотруднику" только если данные сотрудников доступны */}
            {dataAvailability.employees && (
              <label>
                <input
                  type="radio"
                  value="employee"
                  checked={groupBy === "employee"}
                  onChange={(e) => setGroupBy(e.target.value)}
                />
                <span className="checkmark"></span>
                Сотруднику
              </label>
            )}
          </div>
          <div className="analysis-export">
            <button
              type="button"
              className="analysis-export-excel-btn"
              onClick={handleExportExcel}
              disabled={exportExcelLoading}
            >
              {exportExcelLoading ? "Выгрузка…" : "Выгрузить Excel"}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ProjectAnalysis;
